import { enPlural } from "./plural.ts";
import type { Dict } from "./uk.ts";

const n = (value: number) => value.toLocaleString("en-GB");

/** English. Typed as `Dict`, so it has exactly the keys of `uk.ts`. */
export const en: Dict = {
  meta: {
    locale: "en-GB",
    htmlLang: "en",
  },
  brand: {
    name: "Box 33",
    short: "Box 33",
    tagline: "The UKTZED code for any good — with sources, decisions, and BTI",
    values: [
      {
        title: "Researches the product on the web",
        text: "Maker pages, catalogs, and datasheets — with a link to every source.",
      },
      {
        title: "Walks the Ukrainian tariff",
        text: "From the section down to the 10-digit code, with its section and chapter notes.",
      },
      {
        title: "Checks decisions and BTI",
        text: "Ukrainian classification decisions and European BTI for the chosen code.",
      },
    ],
    formCaption: "Customs declaration · box 33 — commodity code",
    form: {
      box31: "Packages and description of goods",
      box31Value: "Deep groove ball bearing SKF 6205-2RSH, 20 pcs",
      box32: "Item No.",
      box33: "Commodity code",
      box34: "Country of origin code",
      box35: "Gross mass (kg)",
      box38: "Net mass (kg)",
    },
  },
  units: {
    hour: "h",
    minute: "m",
    second: "s",
    millisecond: "ms",
    byte: "B",
    kilobyte: "KB",
    megabyte: "MB",
    decimal: ".",
  },
  common: {
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
    close: "Close",
    delete: "Delete",
    deleting: "Deleting…",
    rename: "Rename",
    retry: "Retry",
    refresh: "Refresh",
    open: "Open",
    copy: "Copy",
    copied: "Copied",
    loading: "Loading…",
    back: "Back",
    search: "Search",
    all: "All",
    change: "Change",
    clear: "Clear",
    send: "Send",
    confirm: "Confirm",
    showMore: "Show more",
    showLess: "Show less",
    language: "Language",
    theme: "Theme",
    signOut: "Sign out",
    privacy: "Privacy",
    terms: "Terms of use",
    notFound: "Not found",
    optional: "optional",
    demoData: "Demo data",
    previous: "Previous",
    next: "Next",
    expand: "Expand",
    collapse: "Collapse",
    externalLink: "Opens in a new tab",
    notifications: "Notifications",
    languages: {
      uk: "Українська",
      en: "English",
    },
    themes: {
      system: "System",
      light: "Light",
      dark: "Dark",
    },
  },
  errors: {
    network: "Cannot reach the server. Check your connection and try again.",
    generic: "The request failed.",
    codes: {
      no_platform_key: "This server has no platform key.",
      no_model_access: "No model access is set up. Connect your ChatGPT subscription or an OpenAI API key in Settings, or ask the administrator for access.",
      archive_unavailable: "The session was not deleted: the archive is not available right now. Try again later.",
    },
    loadFailed: "Could not load the data.",
    notFoundTitle: "Page not found",
    notFoundText: "The link may be out of date, or the page was removed.",
    sessionNotFound: "Session not found",
    sessionNotFoundText: "It may have been deleted, or it belongs to another account.",
    toApp: "Back to classifications",
    tryAgain: "Try again",
    unexpected: "Something went wrong",
    unexpectedText: "The page hit an error. Reload it to continue.",
    reload: "Reload the page",
  },
  auth: {
    title: "Sign in",
    welcome: "Welcome to Box 33",
    subtitle: "Sign in to classify goods and keep your session history.",
    google: "Continue with Google",
    approvalNote: "An administrator approves new accounts.",
    devTitle: "Development sign-in",
    devHint: "Available on a local server only.",
    devEmail: "Email",
    devSubmit: "Sign in",
    agreePrefix: "By continuing you agree to the",
    agreeAnd: "and the",
    termsLink: "terms of use",
    privacyLink: "privacy policy",
    agreeSuffix: ".",
    copyright: "© 2026 Box 33",
  },
  waitlist: {
    pendingTitle: "Your account is waiting for approval",
    pendingText: "The administrator reviews every new account. Once access is granted, your workspace opens here.",
    blockedTitle: "Your account is blocked",
    blockedText: "Access to Box 33 is closed. Contact the administrator to restore it.",
    signedInAs: "Signed in as",
    refresh: "Check again",
    stillPending: "The account is not approved yet.",
  },
  shell: {
    newClassification: "New classification",
    searchPlaceholder: "Search by title or code",
    searchLabel: "Search sessions",
    groups: {
      today: "Today",
      yesterday: "Yesterday",
      week: "Previous 7 days",
      older: "Older",
    },
    noSessions: "No classifications yet. Start by describing a good.",
    noMatches: "Nothing found",
    usage: "Usage",
    settings: "Settings",
    admin: "Admin",
    adminBadge: (count: number) => `${count} waiting for you`,
    openMenu: "Open menu",
    closeMenu: "Close menu",
    navigation: "Navigation",
    sessionMenu: "Session actions",
    userMenu: "User menu",
    untitled: "New session",
    renameTitle: "Rename session",
    renameLabel: "Title",
    deleteTitle: "Delete this session?",
    deleteText: (title: string) =>
      `“${title}” will be deleted with its conversation, sources, and files. A copy stays in an archive for one year, then it is deleted; the administrator can erase it sooner on request.`,
    deleted: "Session deleted",
    renamed: "Session renamed",
    statusRunning: "Analysis running",
    statusClassified: "Classified",
    statusClarifying: "Needs clarification",
    statusRejected: "Not classified",
    sessionsLabel: "Sessions",
    dictionary: "UKTZED dictionary",
  },
  session: {
    statusNew: "New",
    statusRunning: "Analyzing…",
    statusClassified: "Classified",
    statusClarifying: "Needs clarification",
    statusRejected: "Not classified",
    exportPdf: "Export PDF",
    exportUnavailable: "PDF export is not available with demo data.",
    panel: "Classification panel",
    showPanel: "Show panel",
    hidePanel: "Hide panel",
    renameHint: "Click to rename",
    segments: {
      label: "Session view",
      chat: "Chat",
      classification: "Classification",
      sources: "Sources",
    },
    readOnly: "Admin view — read only",
    openFile: "Open file",
    jumpToLatest: "Jump to latest",
    actions: "Actions",
    loadingSession: "Loading the session…",
  },
  turn: {
    queued: "Queued",
    running: "Analyzing…",
    doneIn: (duration: string) => `Done in ${duration}`,
    failed: "Failed",
    interrupted: "Interrupted",
    cancelled: "Stopped",
    failedHint: "The analysis stopped with an error.",
    interruptedHint: "The server stopped before the turn finished. Retry continues the same conversation.",
    cancelledHint: "You stopped this analysis.",
    queuedHint: "Waiting for a free worker…",
    retry: "Retry",
    retrying: "Retrying…",
    retryStarted: "The turn is queued again",
    stop: "Stop",
    stopping: "Stopping…",
    stopRequested: "Stopping the analysis",
    process: "Process",
    stepsCount: (count: number) => `${n(count)} ${enPlural(count, "step", "steps")}`,
    noSteps: "No steps yet",
    starting: "Starting…",
    writing: "Writing the reply",
    elapsed: "Elapsed",
    phaseNow: "Now",
    mark: "Box 33",
    toggleStep: "Show step details",
    stepRunning: "Running",
    usage: {
      label: "Turn usage",
      tokens: "Tokens",
      inputOutput: "input / output",
      input: "Input",
      cached: "Cached",
      output: "Output",
      reasoning: "Reasoning",
      searches: "Web searches",
      searchesCount: (count: number) => `${n(count)} ${enPlural(count, "search", "searches")}`,
      apiEquivalent: "API-equivalent",
      apiEquivalentEstimate: "API-equivalent, estimate",
      cacheWrite: "Cache write",
      notReported: "not reported",
      estimateWrites: "The ChatGPT backend does not report cache writes, so all uncached input is counted as written.",
      estimateSearches: "Web searches are counted per query; OpenAI may bill a search with several queries as one call.",
      charged: "Charged",
      source: "Source",
      model: "Model",
    },
    detail: {
      query: "Query",
      url: "URL",
      tool: "Tool",
      arguments: "Arguments",
      result: "Result",
      error: "Error",
      codes: "Codes kept",
      text: "Text",
      started: "Started",
      empty: "No details.",
    },
    kinds: {
      reasoning: "Reasoning",
      search: "Search",
      read: "Read",
      tool: "Tool",
      tariff: "Tariff",
      note: "Note",
      warning: "Warning",
    },
  },
  phases: {
    track: {
      research: "Research",
      tariff: "Tariff",
      decisions: "Decisions",
    },
    raw: {
      research: "Product research",
      research_more: "More research",
      tariff: "Tariff walk",
      code_check: "Code check",
      fact: "Fact lookup",
      evidence: "Stored source lookup",
      lookup: "Web lookup",
      enrichment: "Decisions and BTI",
      box31: "Box 31",
      system: "System",
    },
    pending: "Pending",
    skipped: "Not needed",
    missed: "Not run",
    inProgress: "In progress",
    trackLabel: "Analysis phases",
  },
  messages: {
    historyTitle: "This good was classified before",
    historyText: "Your history has matching classifications. Use a code or run a new analysis.",
    reasonArticle: "Same article",
    reasonSimilar: "Similar",
    declared: "declared",
    chosen: "chosen",
    open: "Open",
    useCode: "Use this code",
    newAnalysis: "Run a new analysis",
    resolvedReused: "A code from an earlier session was used.",
    resolvedNew: "A new analysis was started.",
    classifiedOn: (date: string) => `classified ${date}`,
    reuseCode: "Code",
    reusedFrom: "was taken from session",
    reuseDone: "The code was copied to this session",
    analysisStarted: "A new analysis started",
    you: "You",
    noCode: "no code",
  },
  access: {
    title: "You can't run analyses yet",
    text: "To analyze goods, connect your ChatGPT subscription or an OpenAI API key in Settings, or ask the administrator for access.",
    sharedDown: "The administrator's subscription you were given isn't connected right now. Ask the administrator, or connect your own access in Settings.",
    openSettings: "Open Settings",
    ask: "Ask the administrator",
    asked: (date: string) => `Request sent ${date}. The administrator sees it in Admin.`,
    askAgain: "Remind",
    sent: "Request sent to the administrator",
  },
  composer: {
    placeholder: "Name, article number, manufacturer, material, and purpose of the good",
    placeholderFollowUp: "An answer to the question or more details about the good",
    label: "Message",
    send: "Send",
    attach: "Attach a file",
    attachHint: "PDF, images, DOCX, TXT, CSV · up to 5 files, 25 MB each",
    busyHint: "The agent is working. Wait for the reply or stop the analysis.",
    queuedHint: "The turn is queued. Wait for the reply or stop the analysis.",
    awaitingChoice: "Choose an option above to continue.",
    readOnlyHint: "Admin view: messages cannot be sent.",
    tooMany: "You can attach up to 5 files.",
    tooLarge: (name: string) => `“${name}” is larger than 25 MB.`,
    badType: (name: string) => `The format of “${name}” is not supported.`,
    uploadFailed: (name: string) => `Could not upload “${name}”.`,
    removeFile: (name: string) => `Remove “${name}”`,
    uploading: "Uploading",
    shortcut: "Enter to send · Shift+Enter for a new line",
    sendFailed: "Could not send the message.",
    waitUploads: "Wait for the files to finish uploading.",
  },
  empty: {
    title: "What are we classifying?",
    subtitle: "Describe one good — the agent finds its UKTZED code, duty, decisions, and BTI.",
    examplesLabel: "Examples",
    examples: ["SKF 6205-2RS", "RINOL PU-S602", "Volvo 11037841 roller bearing"],
    tipArticleTitle: "Add the article number and manufacturer",
    tipArticleText: "The exact model helps find the datasheet and the maker's page.",
    tipAttachTitle: "Attach a datasheet or a photo",
    tipAttachText: "A PDF, photo, or spec sheet settles the material and purpose.",
  },
  panel: {
    tabs: {
      classification: "Classification",
      sources: "Sources",
      images: "Images",
      files: "Files",
    },
    empty: "Describe a good — its UKTZED codes, duty, and decisions will appear here.",
    emptyRunning: "The agent is analyzing the good. Codes appear as soon as it settles them.",
    good: "Good",
    facts: {
      articleNumber: "Article",
      brand: "Trade mark",
      manufacturer: "Manufacturer",
      shape: "Shape",
      materials: "Materials",
      originCountry: "Origin",
      description: "Description",
      other: "Other",
    },
    factSources: (count: number) => `${n(count)} ${enPlural(count, "source", "sources")}`,
    factNoSource: "No quote",
    factUnconfirmed: "unconfirmed",
    factSourcesTitle: (label: string) => `Sources: ${label.toLowerCase()}`,
    factUnconfirmedHint: "Only one source confirms this fact. Check it before declaring.",
    factNoQuoteHint: "No quote was found in the stored sources. Check this fact before declaring.",
    citationMessage: "Your message",
    citationOpen: "Open the source",
    quoted: (text: string) => `“${text}”`,
    pictureAlt: (index: number) => `Picture of the good ${index}`,
    pictureOpen: "Open picture",
    pictureSource: "Source page",
    pictureNoSource: "The page this picture was found on is unknown.",
    pictureFile: "Image file",
    pictureCounter: (index: number, total: number) => `${index} of ${total}`,
    allImages: (count: number) => `All images (${n(count)})`,
    stillNeeded: "Still needed",
    stillNeededHint: "An answer in the chat helps choose between the possible codes.",
    chosenCode: "Chosen code",
    copyCode: "Copy code",
    codeCopied: "Code copied",
    confidence: "Confidence",
    duty: (duty: string) => `Duty: ${duty}`,
    reason: "Reasoning",
    ukrainianDecisions: "Ukrainian decisions",
    europeanBti: "European BTI",
    noUkrainianDecision: "No matching Ukrainian decision.",
    noEuropeanBti: "No matching European BTI.",
    declaredCode: "Declared code",
    declaredHint: "The code you put in box 33 of the declaration.",
    useChosen: "Use the chosen code",
    anotherCode: "Another code…",
    declaredSaved: "Declared code saved",
    declaredCleared: "Declared code cleared",
    declaredMatches: "Same as the chosen code",
    declaredDiffers: "Differs from the chosen code",
    declaredMark: "Declared",
    useThisCode: "Use this code",
    useRuledOut: "Use this code…",
    ruledOutTitle: "Use a ruled-out code?",
    ruledOutText: (code: string) =>
      `The agent compared ${code} and ruled it out for the reason below. Put it in box 33 only if you know something about the good that refutes that reason.`,
    feedbackTitle: "Is this code right?",
    feedbackYes: "Yes, the code is right",
    feedbackNo: "No, the code is wrong",
    feedbackDialogTitle: "Which code is right?",
    feedbackDialogText: "An administrator reviews your feedback. It does not change this classification.",
    correctCode: "Correct code",
    reasonLabel: "Why",
    reasonPlaceholder: "For example: the outer diameter is 52 mm, so not subheading 8482 10 10",
    sendFeedback: "Send feedback",
    feedbackThanks: "Thank you! Your feedback goes to review.",
    feedbackSentCorrect: "You marked the code as right",
    feedbackSentWrong: (code: string) => `You proposed ${code}`,
    feedbackAgain: "Change feedback",
    possibleCodes: "Possible codes",
    possibleCodesHint: "Several codes remain — a clarification in the chat settles the right one.",
    comparedCodes: "Compared codes",
    ruledOut: "Why ruled out",
    noChosen: "No code has reached enough confidence yet.",
    rejected: "The agent could not classify this request. Describe one specific good.",
    codeRequired: "Pick a 10-digit code from the tariff",
    tariffTitle: "Tariff title",
  },
  combobox: {
    placeholder: "Code or words from the title",
    hint: "Type at least 2 characters",
    empty: "Nothing found",
    loading: "Searching the tariff…",
    onlyTen: "Only 10-digit codes can be picked",
    pick: "Pick a code",
    label: "Search the tariff",
    error: "Could not search the tariff",
  },
  uktzed: {
    title: "UKTZED dictionary",
    subtitle: "Sections, chapters, headings, and notes of the current tariff.",
    titlesLanguage: "Titles and notes are in Ukrainian, as the tariff publishes them.",
    levels: {
      section: "Section",
      chapter: "Chapter",
      heading: "Heading",
      subheading: "Subheading",
      category: "Category",
      subcategory: "Subcategory",
    },
    levelsShort: {
      section: "section",
      chapter: "chapter",
      heading: "heading",
      subheading: "subheading",
      category: "category",
      subcategory: "subcategory",
    },
    groupLine: "no code",
    groupLineHint: "A title without a code: it groups the codes below it",
    treeLabel: "UKTZED tree",
    loadingLevel: "Loading the level…",
    levelFailed: "Could not load this level.",
    emptyLevel: "No codes at this level.",
    noTariffTitle: "No tariff imported yet",
    noTariffText: "Sections and chapters appear here once an administrator imports the tariff.",
    searchPlaceholder: "Code or words from the title",
    searchLabel: "Search the UKTZED dictionary",
    searchShortcut: "Press / to search",
    searchHint: "Type at least 2 digits of a code or a word of 3 letters.",
    searching: "Searching…",
    searchEmpty: "Nothing found",
    searchEmptyHint: "Try another word or a shorter code.",
    searchFailed: "The search failed.",
    searchMore: (count: number) => `Showing the first ${n(count)}. Refine the query to see the rest.`,
    nearest: "Closest level",
    nearestHint: "This code is not in the current tariff. The closest level:",
    selectTitle: "Nothing selected",
    selectText: "Open a section in the tree or search for a code to see its title, duty, and notes.",
    notFoundTitle: "This code is not in the current tariff",
    notFoundText: (code: string) => `Code ${code} was not found. It may have been changed or removed.`,
    searchFor: (code: string) => `Search for ${code}`,
    breadcrumbs: "Path in the tariff",
    showInTree: "Show in the tree",
    showDetails: "Details",
    dutyTitle: "Import duty rates",
    rates: {
      preferential: "Preferential",
      reduced: "Reduced",
      full: "Full",
    },
    extraUnit: "Additional unit",
    noExtraUnit: "none",
    structure: "Code structure",
    structureOf: (code: string) => `Structure of code ${code}`,
    fullTitle: "Full title",
    allLevels: (count: number) => `All levels (${n(count)})`,
    fewerLevels: "Fewer levels",
    openNode: (name: string) => `Open in the dictionary: ${name}`,
    notesTitle: "Notes",
    noNotes: "This section and chapter have no notes.",
    noteGroup: {
      section: (numeral: string) => `Notes to section ${numeral}`,
      chapter: (code: string) => `Notes to chapter ${code}`,
    },
    noteKinds: {
      notes: "Notes",
      subheading: "Subheading notes",
      additional: "Additional notes",
      ukraine: "Additional note of Ukraine",
    },
    noteNumber: (number: string) => `Note ${number}`,
    notesCount: (count: number) => `${n(count)} ${enPlural(count, "note", "notes")}`,
    mentions: (code: string) => `Mentions ${code}`,
  },
  sources: {
    groups: {
      product: "Product pages",
      decision: "Ukrainian decisions",
      bti: "European BTI",
      tariff: "Tariff",
      search: "Web searches",
      image: "Images",
      upload: "Uploads",
      other: "Other",
    },
    empty: "The agent has not searched or opened anything yet.",
    savedCopy: "Saved copy",
    failed: "Failed",
    blocked: "The site blocked access",
    query: "Query",
    savedAt: (date: string) => `Saved ${date}`,
    docError: "Could not load the saved copy.",
    openOriginal: "Open original",
    noTitle: "Untitled",
    noText: "The page text was not stored.",
    total: (count: number) => `${n(count)} ${enPlural(count, "source", "sources")}`,
  },
  images: {
    empty: "No images yet. Pictures of the good that the agent finds, and images you attach to a message, appear here.",
    pictures: "Pictures of the good",
    uploads: "Your images",
    sources: "Images from sources",
    uploadNote: "You attached this image to a message.",
    sourceLink: "Source",
  },
  files: {
    empty: "No documents yet. Attach a datasheet, a spec sheet, or an invoice to a message; PDFs the agent opens appear here too.",
    uploads: "Your uploads",
    stored: "Stored copies of sources",
    open: "Open",
    kinds: {
      pdf: "PDF",
      image: "Image",
      file: "File",
      page: "Page",
    },
  },
  box31: {
    title: "Box 31",
    titleFor: (code: string) => `Box 31 · ${code}`,
    cardHint: "Description of the good for the declaration",
    generate: "Generate",
    open: "Open",
    regenerate: "Generate again",
    versions: "3 versions",
    needsFacts: (count: number) => `Missing facts: ${n(count)}`,
    outdated: "Outdated",
    conflictBadge: "Does not fit the code",
    failedBadge: "Failed",
    queued: "Queued",
    generating: "Writing",
    description:
      "Item 1: the name and the usual trade description that place the good in this code and no other. The texts are always in Ukrainian.",
    progressQueued: "The generation is queued…",
    progress: "Writing the box 31 texts…",
    progressLookup: "Looking up the missing facts…",
    steps: "Generation steps",
    progressKeep: "The earlier texts stay until the new ones arrive.",
    empty: "No texts for this code yet.",
    emptyHint: "Three versions: concise, standard, and detailed. Each one is enough to classify the good.",
    failed: "The texts could not be generated",
    stopped: "The generation was stopped",
    tryAgain: "Try again",
    stale: "The session's facts changed after the texts were written. Generate them again.",
    conflictTitle: "The description does not support this code",
    versionLabel: "Text version",
    kinds: {
      concise: "Concise",
      standard: "Standard",
      detailed: "Detailed",
    },
    kindHints: {
      concise: "The minimum for classification",
      standard: "Recommended: purpose, properties, trade mark",
      detailed: "Defends the code against its neighbours",
    },
    length: (length: number, limit: number) => `${n(length)} of ${n(limit)} characters`,
    copy: "Copy",
    copied: "Text copied",
    copyFailed: "Could not copy the text",
    copyWarnTitle: "The text has required placeholders",
    copyWarnText: (tokens: string) =>
      `Replace ${tokens} with facts from the documents before you file the declaration. Copy the text with the placeholders?`,
    copyAnyway: "Copy anyway",
    placeholders: "Placeholders",
    required: "Required",
    recommended: "Recommended",
    what: "What to write",
    why: "Why it matters",
    where: "Where to find it",
    warnings: "Check before filing",
    criteria: "What sets the code apart from its neighbours",
    criterionStatus: {
      confirmed: "Confirmed",
      missing: "Fact missing",
      conflict: "Contradicts",
    },
    quotes: "Quotes from the sources",
    otherItems: "You fill in items 2–4",
    items: [
      "2 — number of packages and the package type code, when box 6 is filled.",
      "3 — number and IDs of the containers, or 0, when box 19 is filled.",
      "4 — only for a periodic declaration, its supplementary declaration, or temporary import.",
    ],
    generatedAt: (date: string) => `Generated ${date}`,
    ready: "The box 31 texts are ready",
  },
  settings: {
    title: "Settings",
    subtitle: "Your profile and the model access your classifications use.",
    profile: "Profile",
    profileText: "Name and email come from your Google account.",
    name: "Name",
    email: "Email",
    language: "Interface language",
    languageHint: "The agent replies in the language you pick.",
    languageSaved: "Language changed",
    modelAccess: "Model access",
    modelAccessText: "Every agent turn runs on one of these sources.",
    effective: "In use now",
    nothingConnected: "Nothing connected",
    modes: {
      subscription: "ChatGPT subscription",
      byok: "Own OpenAI API key",
      shared: "Administrator's subscription",
      platform: "Platform key",
      dev: "Development login",
    },
    modesShort: {
      subscription: "Subscription",
      byok: "Own key",
      shared: "Shared",
      platform: "Platform",
      dev: "Dev",
    },
    subscription: {
      text: "Turns run within your ChatGPT plan.",
      connect: "Connect",
      connected: "Connected",
      notConnected: "Not connected",
      disconnect: "Disconnect",
      disconnected: "Subscription disconnected",
      error: "Connection error",
      reconnect: "Reconnect",
      continueLogin: "Continue connecting",
      since: (date: string) => `since ${date}`,
    },
    device: {
      title: "Connect ChatGPT",
      description: "Sign in to OpenAI and confirm the code so Box 33 can work with your plan.",
      step1: "Open the OpenAI sign-in page",
      openPage: "Open the OpenAI page",
      step2: "Enter this code",
      copyCode: "Copy code",
      expiresIn: (time: string) => `The code is valid for ${time}`,
      expired: "The code has expired. Start connecting again.",
      warning:
        "OpenAI will warn you to continue only if you started this login in Codex. Box 33 started it on our server. Continue only if you trust Box 33 with access to your ChatGPT plan.",
      waiting: "Waiting for confirmation in OpenAI…",
      starting: "Preparing the sign-in…",
      succeeded: "ChatGPT connected",
      failed: "Could not connect ChatGPT",
      cancelled: "Connection cancelled",
      cancel: "Cancel sign-in",
      tryAgain: "Start again",
    },
    byok: {
      text: "OpenAI bills this key directly.",
      label: "OpenAI API key",
      placeholder: "sk-…",
      save: "Save key",
      saved: "Key saved",
      remove: "Remove key",
      removed: "Key removed",
      savedLabel: "Saved key",
      show: "Show key",
      hide: "Hide key",
    },
    shared: {
      enabled: (cap: string, spent: string) => `The administrator shares their ChatGPT plan with you: ${cap} a month, ${spent} used (estimated, not charged)`,
      unavailable: "The administrator's subscription is not connected right now.",
    },
    platform: {
      enabled: (cap: string, spent: string) => `Enabled by the administrator: ${cap} per month, ${spent} spent`,
      enabledNoCap: (spent: string) => `Enabled by the administrator without a cap, ${spent} spent`,
      disabled: "Not enabled. Ask the administrator.",
      capUsed: "Cap used",
    },
    dev: {
      text: "The local Codex login on this server. Development only.",
      available: "Available",
    },
    preferred: {
      title: "Preferred source",
      text: "Which source new turns use.",
      automatic: "Automatic",
      automaticText: (platform: boolean) => `Subscription, then own key, then the administrator's subscription${platform ? ", then platform key" : ""}`,
      fixedText: "Always this source",
      saved: "Preferred source saved",
      unavailable: "not connected",
    },
    connectedBadge: "Connected",
    availableBadge: "Available",
    unavailableBadge: "Unavailable",
    offBadge: "Off",
    errorBadge: "Error",
  },
  usage: {
    title: "Usage",
    subtitle: "Turns, tokens, and cost for the month.",
    month: "Month",
    prevMonth: "Previous month",
    nextMonth: "Next month",
    tiles: {
      turns: "Turns",
      inputTokens: "Input tokens",
      outputTokens: "Output tokens",
      webSearches: "Web searches",
      cost: "API-equivalent",
      charged: "Charged",
    },
    cachedShare: (share: string) => `${share} cached`,
    costHint: "What the turns cost at API prices",
    chargedHint: "Platform-key turns only",
    daily: "By day",
    metric: {
      label: "Measure",
      turns: "Turns",
      cost: "Cost",
      tokens: "Tokens",
    },
    chartView: "Chart",
    tableView: "Table",
    day: "Day",
    bySource: "By source",
    recent: "Recent turns",
    columns: {
      session: "Session",
      status: "Status",
      source: "Source",
      duration: "Duration",
      tokens: "Tokens",
      cost: "Cost",
      charge: "Charged",
      date: "Date",
      turns: "Turns",
    },
    empty: "No turns this month.",
    noSource: "—",
    chargeLabel: "charged",
    costLabel: "API",
    turnsCount: (count: number) => `${n(count)} ${enPlural(count, "turn", "turns")}`,
  },
  turnStatus: {
    queued: "Queued",
    running: "Running",
    succeeded: "Done",
    failed: "Failed",
    interrupted: "Interrupted",
    cancelled: "Stopped",
  },
  admin: {
    title: "Admin",
    nav: {
      overview: "Overview",
      tenants: "Tenants",
      turns: "Turns",
      feedback: "Feedback",
      settings: "Settings",
    },
    overview: {
      subtitle: "Tenant status, this month's spend, and reference data.",
      tenants: "Tenants",
      pending: "Waiting for approval",
      active: "Active",
      blocked: "Blocked",
      month: "This month",
      turns: "Turns",
      cost: "API-equivalent",
      charge: "Charged",
      failed: "Failures",
      avgDuration: "Average duration",
      feedbackNew: "New feedback",
      review: "Review",
      data: "Reference data",
      tariff: "UKTZED tariff",
      tariffInfo: (version: string, codes: string) => `Version ${version} · ${codes} codes`,
      ebti: "European BTI",
      ebtiInfo: (rulings: number, valid: number) => `${n(rulings)} ${enPlural(rulings, "ruling", "rulings")}, ${n(valid)} valid`,
      ebtiExport: (date: string) => `EU export of ${date}`,
      ebtiLastCheck: (date: string, outcome: string) => `Last check ${date} — ${outcome}`,
      ebtiOutcome: {
        unchanged: "up to date",
        imported: "imported",
        failed: "failed",
      },
      ebtiNotChecked: "Not checked yet",
      ebtiNextCheck: (date: string) => `Next check ${date}`,
      ebtiLastImport: (date: string, duration: string, added: number, updated: number, removed: number) =>
        `Last import ${date} in ${duration}: ${n(added)} new, ${n(updated)} changed, ${n(removed)} removed`,
      updatedAt: (date: string) => `Updated ${date}`,
      never: "Not loaded yet",
      refreshTariff: "Refresh tariff",
      checkEbti: "Check now",
      jobStarted: "The job is queued",
      jobs: "Recent jobs",
      noJobs: "No jobs yet.",
      jobType: {
        ebti_import: "EBTI update",
        tariff_fetch: "Tariff refresh",
        model_prices_refresh: "Model prices refresh",
      },
      jobStatus: {
        queued: "Queued",
        running: "Running",
        succeeded: "Done",
        failed: "Failed",
        cancelled: "Cancelled",
      },
      columns: {
        type: "Job",
        status: "Status",
        created: "Created",
        updated: "Updated",
        result: "Result",
      },
    },
    tenants: {
      subtitle: "Approve new accounts, give them model access, and set caps.",
      search: "Search by email",
      statusFilter: "Status",
      statuses: {
        pending: "Pending",
        active: "Active",
        blocked: "Blocked",
      },
      columns: {
        tenant: "Tenant",
        status: "Status",
        created: "Created",
        lastLogin: "Last sign-in",
        sources: "Sources",
        access: "Access from you",
        sharedSubscription: "My subscription",
        platformKey: "Platform key",
        cap: "Cap, $/month",
        turns: "Turns",
        cost: "API",
        charge: "Charged",
        actions: "Actions",
        activity: "Activity",
        month: "This month",
      },
      createdOn: (date: string) => `Created ${date}`,
      lastLoginOn: (date: string) => `Signed in ${date}`,
      chargedShort: (value: string) => `${value} charged`,
      approve: "Approve",
      block: "Block",
      unblock: "Unblock",
      noCap: "no cap",
      never: "never",
      updated: "Tenant updated",
      empty: "No tenants match this filter.",
      platformKeyLabel: (email: string) => `Platform key for ${email}`,
      sharedLabel: (email: string) => `Your ChatGPT subscription for ${email}`,
      sharedFrom: (email: string) => `Shared by ${email}`,
      sharedOwn: "Your own account",
      sharedNeedsLogin: "Connect your ChatGPT subscription in Settings to share it.",
      sharedRisk:
        "Turns of the users you pick run on your ChatGPT plan and count against its limits. OpenAI's terms don't allow sharing an account, so it can be suspended. The cap counts their estimated cost; they are not charged.",
      capLabel: (email: string) => `Monthly cap for ${email}`,
      accessRequested: (date: string) => `Asked for access ${date}`,
    },
    turns: {
      subtitle: "Every turn of every tenant. A row opens its trace.",
      status: "Status",
      tenant: "Tenant",
      limit: "Rows",
      anyStatus: "Any status",
      anyTenant: "All tenants",
      columns: {
        tenant: "Tenant",
        session: "Session",
        status: "Status",
        source: "Source",
        model: "Model",
        queuedAt: "Queued at",
        duration: "Duration",
        tokens: "Tokens",
        cost: "Cost",
        error: "Error",
      },
      empty: "No turns match these filters.",
      openTrace: "Open trace",
    },
    trace: {
      title: "Turn trace",
      back: "Back to turns",
      userMessage: "User message",
      steps: "Steps",
      calls: "Agent calls",
      noCalls: "No agent calls.",
      phase: "Phase",
      model: "Model",
      effort: "Effort",
      webSearch: "Web search",
      thread: "Thread",
      resumed: "resumed",
      newThread: "new",
      timings: "Timing",
      start: "Start",
      firstItem: "First item",
      duration: "Duration",
      tokens: "Tokens",
      input: "Input",
      cached: "Cached",
      cacheWrite: "Cache write",
      output: "Output",
      reasoning: "Reasoning",
      searches: "Web searches",
      cost: "Cost",
      schema: "Output schema",
      prompt: "Prompt",
      finalResponse: "Final response",
      items: "Items",
      noItems: "No items.",
      payload: "Payload",
      tenant: "Tenant",
      session: "Session",
      queuedAt: "Queued at",
      source: "Source",
      error: "Error",
      showPrompt: "Show prompt",
      hidePrompt: "Hide prompt",
      showPayload: "Payload",
      callTitle: (seq: number) => `Call ${seq}`,
      none: "—",
      estimate: "estimate",
      longContextApplied: (count: number) =>
        `${n(count)} model ${enPlural(count, "request", "requests")} above the long-context threshold, billed at the long-context rates.`,
      longContextUnknown: "The input is above the long-context threshold, but the per-request token counts are missing, so base rates were used. The cost may be low.",
    },
    feedback: {
      subtitle: "What users said about codes. New first.",
      statuses: {
        new: "New",
        reviewed: "Reviewed",
        dismissed: "Dismissed",
      },
      statusBadge: {
        new: "New",
        reviewed: "Reviewed",
        dismissed: "Dismissed",
      },
      columns: {
        tenant: "Tenant",
        user: "User",
        session: "Session",
        verdict: "Verdict",
        agentCode: "Agent code",
        proposedCode: "Proposed code",
        reason: "Reason",
        date: "Date",
        status: "Status",
        actions: "Actions",
      },
      verdict: {
        correct: "Correct",
        wrong: "Wrong",
      },
      markReviewed: "Reviewed",
      dismiss: "Dismiss",
      reopen: "Move back to new",
      noteTitle: (action: string) => `${action}: note`,
      noteLabel: "Admin note",
      notePlaceholder: "What was done, or why it was dismissed",
      saved: "Feedback updated",
      empty: "No feedback matches this filter.",
      note: "Note",
    },
    settings: {
      subtitle: "Classification model, platform-key billing, and model prices.",
      model: "Classification model",
      modelText:
        "Every tenant's turns run on this model. The list holds the OpenAI models Codex can run for this app: text output, tool calls, structured output, and reasoning at low and medium effort.",
      modelNotOffered: "This model is no longer offered. Turns on it are still priced at its last prices; choose another model.",
      modelOption: (input: string, output: string, context: string) => `in ${input} · out ${output} $ per 1M · context ${context}`,
      billing: "Billing",
      billingText: "A platform-key turn is charged at its API cost plus the markup. models.dev has no web search price, so it is set here.",
      webSearchPrice: "Web search price, $ per call",
      markup: "Platform key markup, %",
      defaultCap: "Default cap, $ per month",
      prices: "Model prices",
      pricesText: "Turn costs are computed from this table, in $ per 1M tokens. It is fetched from models.dev every day and cannot be edited.",
      pricesLegend:
        "Second line: the rate for a request whose input is above the long-context threshold; it applies to the whole request. A dash: no separate rate, so cached input is billed as input and cache writes cost nothing extra.",
      updatedFrom: (date: string, source: string) => `Updated ${date} from ${source}`,
      neverFetched: (source: string) => `Not fetched from ${source} yet`,
      refreshNow: "Refresh now",
      refreshing: "Refreshing…",
      refreshQueued: "The price refresh is queued",
      refreshDone: "Model prices updated",
      refreshFailed: "Could not refresh the model prices",
      lastError: (date: string) => `The last refresh, ${date}, failed`,
      keptPrices: "The table keeps the prices of the last refresh that worked.",
      priceColumns: {
        model: "Model",
        context: "Context",
        input: "Input",
        cached: "Cached input",
        cacheWrite: "Cache write",
        output: "Output",
        longContext: "Long context",
      },
      longContextAbove: (tokens: string) => `above ${tokens}`,
      status: {
        active: "offered",
        deprecated: "deprecated",
        removed: "left models.dev",
      },
      inUse: "in use",
      facts: {
        context: "Context window",
        output: "Longest output",
        efforts: "Reasoning efforts",
        released: "Released",
        knowledge: "Knowledge cutoff",
        fetched: "Fetched",
        prices: "Prices, $ per 1M tokens",
        longContext: (tokens: string) => `Request above ${tokens} tokens`,
      },
      saved: "Settings saved",
      reset: "Reset changes",
      unsaved: "Unsaved changes",
      invalid: "Check the values: numbers must not be negative.",
    },
    session: {
      title: "Tenant session",
      back: "Back",
    },
  },
  legal: {
    draft: "Draft",
    updated: "Version of 4 November 2026",
    backToSignIn: "Back to sign-in",
    backToApp: "Back to the app",
    privacy: {
      title: "Privacy policy",
      intro:
        "This draft explains what data Box 33 processes, why, and for how long it is kept. The final version will be published before the service launches.",
      sections: [
        {
          title: "What we collect",
          body: "Your name, email, and profile picture from your Google account; the descriptions, messages, and files you add to sessions; the pages and documents the agent opens during an analysis; technical logs and model usage records.",
        },
        {
          title: "Why we use it",
          body: "To classify goods, show your session history, meter usage, and prevent abuse. We do not sell data and do not use your sessions for advertising.",
        },
        {
          title: "Who else receives it",
          body: "The description of the good and its attachments go to the model provider (OpenAI) through the source you chose: your ChatGPT subscription, your own key, or the platform key. Public pages the agent read may be kept in a shared cache that is not linked to you. Service emails go through Brevo (EU), which receives the recipient's address and the message, and may record when an email is opened or a link in it is followed.",
        },
        {
          title: "Retention and deletion",
          body: "Sessions are kept until you delete them. A deleted session, with its messages, sources, and files, goes to an archive in the EU for one year and is then deleted. To delete your account entirely, or to have your data erased from the archive too, write to the administrator.",
        },
        {
          title: "Your rights",
          body: "You can get a copy of your data, correct it, or ask for it to be deleted. Contact the service administrator.",
        },
      ],
    },
    terms: {
      title: "Terms of use",
      intro: "This draft sets out the rules for using Box 33. The final version will be published before the service launches.",
      sections: [
        {
          title: "What the service does",
          body: "Box 33 helps identify the UKTZED code of a good: it researches the good, walks the tariff, and shows decisions and BTI. The result is guidance for a professional, not a binding ruling of a customs authority.",
        },
        {
          title: "Your responsibility",
          body: "You choose the code in the customs declaration. Check the reasoning and the sources before you declare. Do not upload data you have no right to share.",
        },
        {
          title: "Accounts",
          body: "An administrator approves each new account. We may block access in case of abuse or unpaid platform-key usage.",
        },
        {
          title: "Payment",
          body: "Turns on your ChatGPT subscription or your own key are paid to OpenAI directly. Platform-key turns are metered at the published prices with a markup.",
        },
        {
          title: "Limitation of liability",
          body: "The service is provided as is. We are not liable for decisions made on the basis of classification results or for the consequences of incorrect declarations.",
        },
      ],
    },
  },
};
